import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { UiControlPropsType } from 'shared/types';
import { ReservationModel, ReservationPlaceModel } from 'shared/models';
import { UiModalTitle } from 'shared/uikit-new';
import { toDate } from 'shared/utilities';
import { reservationPlacesQuery } from 'shared/queries';
import { useAsyncEffect, useObservable, useMedia } from 'shared/hooks';

import { UiIcon } from '../UiIcon';
import { UiModal } from '../UiModal';
import { UiModalContent } from '../UiModalContent';
import { UiModalActions } from '../UiModalActions';
import { UiButton } from '../UiButton';
import { UiReservationPicker } from '../UiReservationPicker';

import './styles.scss';

type PropsType = UiControlPropsType<ReservationModel | null> & {
    date: string;
};

export const UiReservationSelect = observer(function UiReservationSelect(
    {
        date,
        value,
        onChange = () => undefined,
        name = 'reservation',
    }: PropsType,
) {
    const store = useObservable({
        reservation: null as ReservationModel | null,
        reservationPlace: new ReservationPlaceModel(),
        reservationPlaces: [] as ReservationPlaceModel[],
        isOpened: false,
        isModalOpened: false,
    });

    const { isDesktop } = useMedia();

    useEffect(() => {
        store.set('reservation', value ? new ReservationModel(value) : null);
    }, [value, store]);

    useAsyncEffect(async () => {
        const response = await reservationPlacesQuery(date);
        if (response.data) {
            store.set(
                'reservationPlaces',
                response.data.items.map(item => new ReservationPlaceModel(item)),
            );
        }
    }, [date]);

    const classNames = classnames('ui-new-reservation-select', {
        'ui-new-reservation-select--opened': store.isOpened,
        'ui-new-reservation-select--empty': !value
    });

    return (
        <div className={classNames}>
            {!value && (
                <div className="ui-new-reservation-select-control" onClick={() => store.set('isOpened', !store.isOpened)}>
                    <div className="ui-new-reservation-select-control__icon">
                        <UiIcon.MessageSquareDiff size={24} />
                    </div>
                    <div className="ui-new-reservation-select-control__placeholder">Добавить</div>
                    <div className="ui-new-reservation-select-control__chevron">
                        <UiIcon.ChevronDown size={16} />
                    </div>
                </div>
            )}
            {!!value && (
                <div
                    className="ui-new-reservation-select-control"
                    onClick={() => {
                        const reservationPlace = store.reservationPlaces.find(item => item.id === value.reservationPlaceId)
                            || value.reservationPlace;
                        store.update({
                            reservation: new ReservationModel(value),
                            reservationPlace,
                            isModalOpened: true,
                        });
                    }}
                >
                    <div className="ui-new-reservation-select-item ui-new-reservation-select-item--active">
                        <div className="ui-new-reservation-select-item__image" style={{ backgroundImage: `url(${value.reservationPlace.previewImageUrl})` }} />
                        <div className="ui-new-reservation-select-item__inner">
                            <div className="ui-new-reservation-select-item__name">{value.reservationPlace.name}</div>
                            <div
                                className="ui-new-reservation-select-item__action ui-new-reservation-select-item__action--clear"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onChange({
                                        name,
                                        value: null,
                                    });
                                }}
                            >
                                <UiIcon.Trash2 size={16} />
                                <span>Удалить бронирование</span>
                            </div>
                        </div>
                        <div className="ui-new-reservation-select-item__info">
                            <div>{value.startAtDate.format('D MMMM YYYY')}</div>
                            <div>{value.startAtDate.format('HH:mm')}-{value.endAtDate.format('HH:mm')}</div>
                        </div>
                    </div>
                </div>
            )}

            {!isDesktop && (
                <UiModal
                    isOpened={store.isOpened}
                    onClose={() => store.set('isOpened', false)}
                >
                    <div className="ui-new-reservation-select__items">
                        {store.reservationPlaces.map(reservationPlace => (
                            <div
                                key={reservationPlace.id}
                                className="ui-new-reservation-select-item"
                                onClick={() => {
                                    store.update({
                                        reservation: new ReservationModel({
                                            ...(value || {}),
                                            reservationPlaceId: reservationPlace.id,
                                        }),
                                        reservationPlace,
                                        isOpened: false,
                                        isModalOpened: true,
                                    });
                                }}
                            >
                                <div className="ui-new-reservation-select-item__image" style={{ backgroundImage: `url(${reservationPlace.previewImageUrl})` }} />
                                <div className="ui-new-reservation-select-item__inner">
                                    <div className="ui-new-reservation-select-item__name">{reservationPlace.name}</div>
                                    <div className="ui-new-reservation-select-item__action">Добавить</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </UiModal>
            )}
            {isDesktop && (
                <div className="ui-new-reservation-select__items">
                    {store.reservationPlaces.map(reservationPlace => (
                        <div
                            key={reservationPlace.id}
                            className="ui-new-reservation-select-item"
                            onClick={() => {
                                store.update({
                                    reservation: new ReservationModel({
                                        ...(value || {}),
                                        reservationPlaceId: reservationPlace.id,
                                    }),
                                    reservationPlace,
                                    isOpened: false,
                                    isModalOpened: true,
                                });
                            }}
                        >
                            <div className="ui-new-reservation-select-item__image" style={{ backgroundImage: `url(${reservationPlace.previewImageUrl})` }} />
                            <div className="ui-new-reservation-select-item__inner">
                                <div className="ui-new-reservation-select-item__name">{reservationPlace.name}</div>
                                <div className="ui-new-reservation-select-item__action">Добавить</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {store.reservation && (
                <UiModal isOpened={store.isModalOpened && !!store.reservation} onClose={() => store.set('isModalOpened', false)} isAside>
                    <UiModalTitle>Забронировать на {toDate(date).format('DD MMMM')}</UiModalTitle>
                    <UiModalContent>
                        <UiReservationPicker reservationPlace={store.reservationPlace} value={store.reservation} date={date} name={'reservation'} onChange={store.handleChange} />
                    </UiModalContent>
                    <UiModalActions>
                        <UiButton
                            onClick={() => {
                                store.update({
                                    isModalOpened: false,
                                    reservation: null,
                                });
                                store.set('isModalOpened', false);
                            }}
                        >
                            Отменить
                        </UiButton>
                        <UiButton
                            isDisabled={!store.reservation.startAt || !store.reservation.endAt}
                            onClick={() => {
                                if (store.reservation) {
                                    onChange({
                                        name,
                                        value: new ReservationModel({
                                            ...store.reservation,
                                            reservationPlace: store.reservationPlace,
                                        }),
                                    });
                                }
                                store.set('isModalOpened', false);
                            }}
                        >
                            Продолжить
                        </UiButton>
                    </UiModalActions>
                </UiModal>
            )}
        </div>
    );
});
